import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminDeleteFaq,
  adminGetContentSettings,
  adminListFaqs,
  adminSaveAnnouncement,
  adminSaveLandingCopy,
  adminUpsertFaq,
  type AnnouncementValue,
  type FaqRow,
  type LandingCopyValue,
} from "@/lib/admin-content.functions";

export const Route = createFileRoute("/_authenticated/admin/content")({
  component: ContentPage,
});

function ContentPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Content & FAQ</h1>
        <p className="text-sm text-muted-foreground">
          Manage FAQs, the global announcement banner, and landing-page copy.
        </p>
      </div>
      <Tabs defaultValue="faqs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="banner">Announcement</TabsTrigger>
          <TabsTrigger value="landing">Landing copy</TabsTrigger>
        </TabsList>
        <TabsContent value="faqs">
          <FaqsTab />
        </TabsContent>
        <TabsContent value="banner">
          <AnnouncementTab />
        </TabsContent>
        <TabsContent value="landing">
          <LandingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FaqsTab() {
  const qc = useQueryClient();
  const list = useServerFn(adminListFaqs);
  const upsert = useServerFn(adminUpsertFaq);
  const del = useServerFn(adminDeleteFaq);

  const { data } = useQuery({
    queryKey: ["admin", "faqs"],
    queryFn: () => list(),
  });

  const upsertMut = useMutation({
    mutationFn: (input: Omit<FaqRow, "id"> & { id?: string }) => upsert({ data: input }),
    onSuccess: () => {
      toast.success("FAQ saved");
      qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("FAQ deleted");
      qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() =>
            upsertMut.mutate({
              question: "New question",
              answer: "Answer goes here…",
              sort_order: (data?.faqs.length ?? 0) * 10,
              published: false,
            })
          }
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add FAQ
        </Button>
      </div>
      <div className="space-y-2">
        {(data?.faqs ?? []).map((faq) => (
          <FaqCard
            key={faq.id}
            faq={faq}
            onSave={(v) => upsertMut.mutate({ ...v, id: faq.id })}
            onDelete={() => delMut.mutate(faq.id)}
          />
        ))}
        {data && data.faqs.length === 0 && (
          <Card className="border-dashed border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
            No FAQs yet. Add your first one above.
          </Card>
        )}
      </div>
    </div>
  );
}

function FaqCard({
  faq,
  onSave,
  onDelete,
}: {
  faq: FaqRow;
  onSave: (v: Omit<FaqRow, "id">) => void;
  onDelete: () => void;
}) {
  const [question, setQuestion] = useState(faq.question);
  const [answer, setAnswer] = useState(faq.answer);
  const [order, setOrder] = useState(faq.sort_order);
  const [published, setPublished] = useState(faq.published);
  const dirty =
    question !== faq.question ||
    answer !== faq.answer ||
    order !== faq.sort_order ||
    published !== faq.published;

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Question"
            className="font-medium"
          />
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Answer"
            rows={3}
          />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Order</Label>
              <Input
                type="number"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value) || 0)}
                className="h-7 w-20"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={published} onCheckedChange={setPublished} />
              <Label className="text-xs">Published</Label>
            </div>
            {!published && (
              <Badge variant="outline" className="border-yellow-500/50 text-yellow-500">
                Draft
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            disabled={!dirty}
            onClick={() =>
              onSave({ question, answer, sort_order: order, published })
            }
          >
            <Save className="mr-1 h-3.5 w-3.5" /> Save
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete FAQ?</AlertDialogTitle>
                <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Card>
  );
}

function AnnouncementTab() {
  const qc = useQueryClient();
  const get = useServerFn(adminGetContentSettings);
  const save = useServerFn(adminSaveAnnouncement);

  const { data } = useQuery({
    queryKey: ["admin", "content-settings"],
    queryFn: () => get(),
  });

  const [val, setVal] = useState<AnnouncementValue>({ text: "", type: "info", active: false });
  useEffect(() => {
    if (data?.announcement) setVal(data.announcement);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => save({ data: val }),
    onSuccess: () => {
      toast.success("Announcement saved");
      qc.invalidateQueries({ queryKey: ["admin", "content-settings"] });
      qc.invalidateQueries({ queryKey: ["public", "content"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Banner text</Label>
          <Textarea
            value={val.text}
            onChange={(e) => setVal({ ...val, text: e.target.value })}
            placeholder="e.g. Scheduled maintenance Sunday at 02:00 UTC."
            rows={2}
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select
              value={val.type}
              onValueChange={(v) => setVal({ ...val, type: v as AnnouncementValue["type"] })}
            >
              <SelectTrigger className="h-8 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="success">Success</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-5">
            <Switch
              checked={val.active}
              onCheckedChange={(c) => setVal({ ...val, active: c })}
            />
            <Label className="text-xs">Show banner on landing page</Label>
          </div>
          <div className="ml-auto pt-5">
            <Button size="sm" onClick={() => saveMut.mutate()}>
              <Save className="mr-1 h-3.5 w-3.5" /> Save banner
            </Button>
          </div>
        </div>
        {val.active && val.text && (
          <div
            className={`mt-2 rounded border p-3 text-sm ${
              val.type === "warning"
                ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-500"
                : val.type === "success"
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
                  : "border-primary/40 bg-primary/10 text-primary"
            }`}
          >
            <div className="text-[10px] uppercase tracking-widest opacity-70">Preview</div>
            <div>{val.text}</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function LandingTab() {
  const qc = useQueryClient();
  const get = useServerFn(adminGetContentSettings);
  const save = useServerFn(adminSaveLandingCopy);

  const { data } = useQuery({
    queryKey: ["admin", "content-settings"],
    queryFn: () => get(),
  });

  const [val, setVal] = useState<LandingCopyValue>({});
  useEffect(() => {
    if (data?.landing) setVal(data.landing);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => save({ data: val }),
    onSuccess: () => {
      toast.success("Landing copy saved");
      qc.invalidateQueries({ queryKey: ["admin", "content-settings"] });
      qc.invalidateQueries({ queryKey: ["public", "content"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field
          label="Hero eyebrow"
          value={val.hero_eyebrow ?? ""}
          onChange={(v) => setVal({ ...val, hero_eyebrow: v })}
          placeholder="Live · 6 majors scanned every tick"
        />
        <Field
          label="CTA label"
          value={val.cta_label ?? ""}
          onChange={(v) => setVal({ ...val, cta_label: v })}
          placeholder="Start trading smarter"
        />
        <div className="md:col-span-2">
          <Field
            label="Hero title"
            value={val.hero_title ?? ""}
            onChange={(v) => setVal({ ...val, hero_title: v })}
            placeholder="AI-Powered Forex Signals…"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Hero subtitle</Label>
          <Textarea
            value={val.hero_subtitle ?? ""}
            onChange={(e) => setVal({ ...val, hero_subtitle: e.target.value })}
            placeholder="ForexMind AI fuses multi-timeframe…"
            rows={3}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button size="sm" onClick={() => saveMut.mutate()}>
          <Save className="mr-1 h-3.5 w-3.5" /> Save copy
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Empty fields fall back to the built-in defaults on the landing page.
      </p>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
